import { Autocomplete, FormControl, FormHelperText, TextField } from "@mui/material";
import { orderBy } from "lodash-es";
import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { NoNullFields } from "../../../../server/supabase/base-supabase-service";
import type { UsableAccountPersonField } from "../../server/supabase-segment-field-service";
import type { Segment, SegmentMode } from "./types";

interface SegmentAudienceProps {
  segment: Segment["segment_json"]["segments"][number];
  segmentFields: NoNullFields<UsableAccountPersonField>[];
  handleFieldChange: (value: string) => void;
  disabled?: boolean;
  errMsg?: string;
  mode: SegmentMode;
}

export const SegmentAudience: FC<SegmentAudienceProps> = ({
  segment,
  segmentFields,
  handleFieldChange,
  disabled,
  errMsg,
  mode,
}) => {
  const { t } = useTranslation();

  const audiences = useMemo(
    () =>
      orderBy(
        segmentFields.filter((f) => f.key.startsWith("recipient_type_")),
        (f) => f.display_name.toLowerCase(),
      ),
    [segmentFields],
  );

  const value = useMemo(() => {
    const val = audiences.find((f) => f.key === segment.recipient_type_key);
    if (val) return val;

    return mode === "SEGMENT" ? audiences.find((x) => x.usable) : null;
  }, [segment.recipient_type_key, audiences, mode]);

  return (
    <FormControl sx={{ mr: 1, minWidth: 250 }} error={Boolean(errMsg)}>
      <Autocomplete
        size="small"
        disablePortal={false}
        options={audiences}
        getOptionLabel={(option) => option.display_name}
        disableClearable={mode === "SEGMENT"}
        onChange={(_, val) => {
          if (!val) return;

          handleFieldChange(val.key);
        }}
        value={value}
        disabled={disabled}
        renderInput={(params) => <TextField {...params} label={t("Audience")} variant="outlined" />}
      />
      {Boolean(errMsg) && <FormHelperText>{errMsg}</FormHelperText>}
    </FormControl>
  );
};
